import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './TabsBar.module.scss';
import Badge from '../../Badge/Badge';

export interface Tab {
  label: string;
  key: string;
  value?: number | null;
  badgeNumber?: number;
  hasError?: boolean;
  onClick?: () => void;
  isRemovable?: boolean;
}

interface TabsBarProps {
  tabs: Tab[];
  value?: string;
  onChange: (key: string) => void;
  confirmOnTabChange?: () => boolean;
}

export default function TabsBar({ tabs, value, onChange, confirmOnTabChange }: TabsBarProps) {
  const activeIndex = tabs.findIndex(t => t.key === value);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Update indicator
  useEffect(() => {
    const el = tabRefs.current[activeIndex];
    if (!el) return;

    setIndicatorStyle({
      transform: `translateX(${el.offsetLeft}px)`,
      width: el.offsetWidth,
      backgroundColor: tabs[activeIndex]?.hasError ? 'var(--status-error-full)' : undefined,
    });
  }, [activeIndex, tabs]);

  const selectTab = (index: number) => {
    const tab = tabs[index];
    onChange(tab.key);
    tab.onClick?.();
  };

  const handleClick = (index: number) => {
    if (index === activeIndex) return;

    if (confirmOnTabChange && confirmOnTabChange()) {
      setPendingIndex(index);
      setIsConfirmOpen(true);
    } else {
      selectTab(index);
    }
  };

  const confirmChange = (confirmed: boolean) => {
    setIsConfirmOpen(false);
    if (confirmed && pendingIndex != null) {
      selectTab(pendingIndex);
    }
    setPendingIndex(null);
  };

  return (
    <div className={styles.root}>
      <div className="flex relative gap-x-24">
        {tabs.map((tab, index) => (
          <div key={tab.key} className="flex flex-shrink-0 pb-17">
            <div
              ref={el => {
                tabRefs.current[index] = el;
              }}
              className={classNames('flex cursor-pointer typo-text-16', {
                'text-full font-semibold': index === activeIndex && !tab.hasError,
                'status-error-full': tab.hasError,
                'text-faded': index !== activeIndex && !tab.hasError,
              })}
              onClick={() => handleClick(index)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') handleClick(index);
              }}
              role="button"
              tabIndex={0}>
              {tab.label}

              {typeof tab.value === 'number' && tab.value > 0 && (
                <div className="pl-6">
                  <Badge expanded dotFilled label={String(tab.value)} type={tab.hasError ? 'danger' : 'neutral'} />
                </div>
              )}

              {tab.badgeNumber && tab.badgeNumber > 0 && (
                <div className="pl-6">
                  <Badge
                    expanded
                    dotFilled
                    label={String(tab.badgeNumber)}
                    type={tab.hasError ? 'danger' : 'neutral'}
                  />
                </div>
              )}
            </div>
          </div>
        ))}

        <div className={styles.indicator} style={indicatorStyle} />
      </div>

      {isConfirmOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-50">
          <div className="surface-elevated p-6 rounded shadow-lg">
            <p>Are you sure you want to switch tabs?</p>
            <div className="mt-4 flex justify-end gap-3">
              <button type="button" className="px-3 py-1 border rounded" onClick={() => confirmChange(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1 bg-primary text-white rounded"
                onClick={() => confirmChange(true)}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
